import { getSettings } from "@/lib/bank";

const synth = typeof window !== "undefined" ? window.speechSynthesis : null;
let voices = [];
// Natural / HD voices first; novelty and legacy robotic voices are dropped
const PREFERRED = [/google us english/i, /natural/i, /neural/i, /samantha/i, /\bava\b/i, /jenny/i, /aria/i, /microsoft (zira|guy)/i, /enhanced|premium/i, /google uk english/i];
const ROBOTIC = /espeak|\b(fred|albert|zarvox|trinoids|bahh|bells|boing|bubbles|cellos|jester|organ|superstar|whisper|wobble|ralph|junior|kathy|hysterical|deranged|bad news|good news|grandma|grandpa|rocko|shelley|sandy|flo|eddy|reed)\b/i;
const prefScore = (v) => { const i = PREFERRED.findIndex((r) => r.test(v.name)); return i < 0 ? PREFERRED.length : i; };
const rank = (v) => prefScore(v) * 100 + (/^en[-_]US/i.test(v.lang) ? 0 : /^en[-_]CA/i.test(v.lang) ? 1 : 2) * 4 + (v.localService ? 0 : 2) + (v.default ? 0 : 1);
export const loadVoices = () => {
  if (!synth) return [];
  const en = synth.getVoices().filter((v) => /^en[-_](US|CA|GB)/i.test(v.lang));
  const clean = en.filter((v) => !ROBOTIC.test(v.name));
  voices = (clean.length ? clean : en).sort((a, b) => rank(a) - rank(b));
  return voices;
};
let voicesReadyP = null;
// Voices load asynchronously on Android/iOS; wait for them (max 1.5s) before speaking
export const voicesReady = () => {
  if (!synth) return Promise.resolve([]);
  if (loadVoices().length) return Promise.resolve(voices);
  if (!voicesReadyP) {
    voicesReadyP = new Promise((res) => {
      const done = () => { voicesReadyP = null; res(loadVoices()); };
      synth.addEventListener?.("voiceschanged", done, { once: true });
      setTimeout(done, 1500);
    });
  }
  return voicesReadyP;
};
if (synth) { loadVoices(); synth.onvoiceschanged = loadVoices; }

const pickVoice = (offset) => {
  const vs = voices.length ? voices : loadVoices();
  if (!vs.length) return null;
  const pref = getSettings().voiceURI;
  const base = Math.max(0, vs.findIndex((v) => v.voiceURI === pref));
  return vs[(base + offset) % vs.length];
};

export const cancelSpeech = () => { try { synth?.cancel(); } catch { /* noop */ } };

// Mobile browsers only allow audio after a user gesture: call this synchronously inside a tap handler
let audioCtx = null, speechUnlocked = false;
export const unlockAudio = (force = false) => {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (AC) {
      audioCtx = audioCtx || new AC();
      if (audioCtx.state === "suspended") audioCtx.resume().catch(() => {});
      const src = audioCtx.createBufferSource();
      src.buffer = audioCtx.createBuffer(1, 1, 22050);
      src.connect(audioCtx.destination); src.start(0);
    }
  } catch { /* noop */ }
  if (synth && (!speechUnlocked || force)) {
    speechUnlocked = true;
    try { synth.resume(); const u = new SpeechSynthesisUtterance(" "); u.volume = 0; u.lang = "en-US"; synth.speak(u); } catch { /* noop */ }
  }
};
export const installAudioUnlock = () => {
  ["touchend", "pointerdown", "click", "keydown"].forEach((ev) => window.addEventListener(ev, () => unlockAudio(), { capture: true, passive: true }));
};

const wait = (ms) => new Promise((r) => setTimeout(r, ms));

// Resolves {ok:true} when the utterance played, {ok:false} if it errored or never started (blocked on mobile)
export const DEFAULT_RATE = 0.75;
export const speak = async (text, { rate = DEFAULT_RATE, voice = 0 } = {}) => {
  if (!synth) return { ok: false, reason: "unsupported" };
  await voicesReady();
  const busy = synth.speaking || synth.pending;
  synth.cancel();
  if (busy) await wait(80);
  synth.resume();
  return new Promise((resolve) => {
    let done = false, started = false;
    const finish = (r) => { if (!done) { done = true; clearTimeout(startGuard); clearTimeout(endGuard); resolve(r); } };
    const u = new SpeechSynthesisUtterance(text);
    const v = pickVoice(voice);
    if (v) u.voice = v;
    u.lang = v?.lang?.replace("_", "-") || "en-US";
    u.volume = 1.0; u.rate = rate; u.pitch = 1.0;
    u.onstart = () => { started = true; };
    u.onend = () => finish({ ok: true });
    u.onerror = (e) => finish(e.error === "interrupted" || e.error === "canceled" ? { ok: true, cancelled: true } : { ok: false, reason: e.error || "error" });
    const startGuard = setTimeout(() => { if (!started) { synth.cancel(); finish({ ok: false, reason: "blocked" }); } }, 3500);
    const endGuard = setTimeout(() => finish({ ok: true }), (text.split(/\s+/).length * 600) / rate + 6000);
    synth.speak(u);
  });
};

export const playItemAudio = async (partKey, item, rate, isCancelled = () => false) => {
  // Clear gaps so each Sentence Build phrase and conversation turn is heard on its own
  const seq = partKey === "C" ? item.chunks.map((t, i) => [t, 0, i < item.chunks.length - 1 ? 380 : 0])
    : partKey === "D" ? [...item.turns.map((t, i) => [t.text, i % 2, 450]), [item.question, 2, 0]]
      : [[item.text, 0, 0]];
  for (const [t, voice, gap] of seq) {
    if (isCancelled()) return true;
    const r = await speak(t, { rate, voice });
    if (!r.ok) return false;
    if (gap) await wait(gap);
  }
  return true;
};

const SR = typeof window !== "undefined" ? window.SpeechRecognition || window.webkitSpeechRecognition : null;
export const speechRecognitionSupported = !!SR;

export const createRecognizer = (onText, onState) => {
  if (!SR) return null;
  const rec = new SR();
  rec.lang = "en-US"; rec.continuous = true; rec.interimResults = true; rec.maxAlternatives = 1;
  let active = false, running = false, finalText = "", lastText = "";
  rec.onstart = () => { running = true; if (active) onState?.("recording"); else { try { rec.stop(); } catch { /* noop */ } } };
  rec.onresult = (e) => {
    let interim = "";
    for (let i = e.resultIndex; i < e.results.length; i++) {
      const r = e.results[i];
      if (r.isFinal) finalText += r[0].transcript + " "; else interim += r[0].transcript;
    }
    lastText = (finalText + interim).trim();
    restarts = 0;
    if (active) onText(lastText);
  };
  rec.onerror = (e) => {
    if (["not-allowed", "service-not-allowed", "audio-capture"].includes(e.error)) { active = false; onState?.("denied"); }
    else if (e.error === "network") onState?.("network");
  };
  rec.onend = () => {
    running = false;
    // keep interim words that never became final before a restart
    if (lastText.length > finalText.trim().length) finalText = lastText + " ";
    if (active) { restarts++; setTimeout(() => { if (active && !running) safeStart(); }, Math.min(150 * restarts, 2000)); }
    else releaseLock();
  };
  let restarts = 0;
  let releaseLock = () => {};
  const safeStart = () => { try { rec.start(); } catch { /* already running */ } };
  return {
    async start() {
      active = true; finalText = ""; lastText = "";
      await recognizerLock; // Chrome only allows one live recognizer; wait for the previous to end
      if (!active) return;
      recognizerLock = new Promise((res) => { let done = false; releaseLock = () => { if (!done) { done = true; res(); } }; setTimeout(releaseLock, 1200); });
      safeStart();
    },
    stop() { active = false; try { rec.stop(); } catch { releaseLock(); } },
  };
};
let recognizerLock = Promise.resolve();
